// Import necessary dependencies and components
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JobListSection } from '../components/home/JobListSection'; // Component to display the list of jobs
import { JobHero } from '../components/job/jobFilters/JobHero'; // Header section with search functionality
import { JobFilters } from '../components/job/jobFilters'; // Filtering options for jobs
import { useJobDetails } from '../hooks/useJobDetails'; // Custom hook for managing selected job details
import { useJobSearch } from '../hooks/useJobSearch'; // Custom hook for job search and filtering logic

// Main component for the Jobs page
export function Jobs() {
  // State for the search term entered by the user
  const [searchTerm, setSearchTerm] = useState('');

  // State for job filters (e.g., departments, contract type, and state)
  const [jobFilters, setJobFilters] = useState({
    departments: [] as string[], // Selected departments (array of strings)
    contract: '', // Selected contract type
    state: '', // Selected job state
  });

  // Custom hook to manage job search logic
  const {
    jobs, // List of all jobs
    filteredJobs, // Jobs filtered based on search term and filters
    loading, // Loading state for job fetching
    error, // Error state for job fetching
    page, // Current pagination page
    itemsPerPage, // Number of items per page
    setPage, // Function to update the page
  } = useJobSearch(searchTerm, jobFilters);

  // Custom hook to manage selected job details
  const { selectedJobId, selectJob, clearSelection } = useJobDetails();

  // For navigation to the job details page
  const navigate = useNavigate();

  // Function to handle changes in job filters
  const handleFilterChange = (updatedFilters: typeof jobFilters) => {
    setJobFilters(updatedFilters); // Update the filters
    clearSelection(); // Clear the currently selected job
    setPage(1); // Reset pagination to the first page
  };

  // Function to handle job selection
  const handleJobSelect = (jobId: number) => {
    selectJob(jobId); // Mark the job as selected
    navigate(`/jobs/${jobId}`); // Navigate to the job details page
  };

  // Main JSX rendering
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero section with search functionality */}
      <JobHero searchTerm={searchTerm} onSearchChange={setSearchTerm} />

      {/* Filters section */}
      <div className="container mx-auto px-4 -mt-8 relative z-10">
        <JobFilters
          filters={jobFilters} // Current filter state
          jobs={jobs} // All jobs (unfiltered)
          onFilterChange={handleFilterChange} // Callback for filter updates
        />
      </div>

      {/* Job list section */}
      <JobListSection
        jobs={filteredJobs} // Jobs after applying filters and search
        page={page} // Current pagination page
        itemsPerPage={itemsPerPage} // Number of items per page
        loading={loading} // Loading state
        error={error} // Error state
        selectedJobId={selectedJobId} // ID of the currently selected job
        onSelectJob={handleJobSelect} // Callback for selecting a job
        onPageChange={setPage} // Callback for pagination changes
      />
    </div>
  );
}
